import { Box, Typography, Button, MenuItem, Select, FormControl, InputLabel, TextField, CircularProgress, Autocomplete } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DownloadIcon from '@mui/icons-material/Download';
import { Toast } from 'primereact/toast';
import CustomAxios from "../../utils/CustomAxios";
import { summaryFor, chartFor } from "./reportSummary";

/**
 * User and order analysis.
 *
 * Replaces the spreadsheet workflow behind "Shino User Order Analysis Report".
 * The filters do not build SQL -- they name a report the server already knows,
 * and it refuses a name it does not recognise. A free-text query box here would
 * be a read of the users, payments and staff tables for anyone who reached the
 * screen.
 *
 * The page is built so the question is answered before the table is read: four
 * figures, then a chart where the shape matters, then the rows. Every one of
 * those is derived from the same response, so nothing on screen can disagree
 * with anything else on screen -- or with the download, which is the same
 * request again.
 */

/** Must match REPORTS in the backend's user_reports.py. */
const REPORTS = [
    { key: 'users_by_segment', label: 'By segment' },
    { key: 'users_by_hub', label: 'By hub' },
    { key: 'segment_by_hub', label: 'Segment × hub' },
    { key: 'signups_by_hub_day', label: 'Signups' },
    { key: 'user_list', label: 'User list' },
    { key: 'orders_by_category', label: 'By category' },
    { key: 'orders_by_product', label: 'By product' },
];

/** Reached by clicking a row, not from the picker. */
const DRILL_REPORT = 'orders_in_category';
const DRILL_PRODUCT = 'orders_in_product';

/** The product filter's own source. Not shown in the picker: it is a list to
 *  choose from, not a report to read. */
const PRODUCT_OPTIONS = 'product_options';

/** Reports whose rows lead somewhere. Drives the pointer cursor and the chevron
 *  together, so a row can never look clickable without being clickable. */
const DRILLABLE = ['orders_by_category', 'orders_by_product'];

/** Must match SEGMENTS in user_reports.py. */
const SEGMENTS = [
    'Active Recurring User',
    'Repeat User',
    'Single Order User',
    'Churned User',
    'Never Ordered',
];

/** Which filters each report actually uses. Hidden, not disabled: a control
 *  that cannot do anything is noise, and greying it out still asks to be read. */
const USES_SEGMENT = ['users_by_hub', 'user_list'];
const USES_HUB = ['users_by_segment', 'user_list', 'orders_by_category', DRILL_REPORT,
    'orders_by_product', DRILL_PRODUCT];
// The category and product reports scan orders rather than aggregating, so a
// range is worth offering -- but left empty it means all time, which is what the
// server does.
const USES_DATES = ['signups_by_hub_day', 'orders_by_category', DRILL_REPORT,
    'orders_by_product', DRILL_PRODUCT];
/* The product picker. On the ranking it narrows to one row while leaving its
 * share of the hub intact; on the drill-down it is what is being drilled into,
 * so the server refuses the request without it. */
const USES_PRODUCT = ['orders_by_product', DRILL_PRODUCT];

const PINK = '#ED1E79';
const INK = '#16121A';
const MUTED = '#6E6478';
const LINE = '#E9E3EE';
const CHIP = '#F4EFF7';

/** One colour per segment, used by the chip, the chart and the share bar so a
 *  segment looks the same wherever it appears. */
const SEGMENT_COLOUR = {
    'Active Recurring User': '#0B7A5E',
    'Repeat User': '#3B7DD8',
    'Single Order User': '#C2820B',
    'Churned User': '#ED1E79',
    'Never Ordered': '#7A4FE0',
};

const TONE = { brand: '#7A4FE0', good: '#0B7A5E', accent: PINK, muted: '#9CA3AF' };

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export function UserReports() {
    const toast = useRef(null);
    const inventory = useSelector((state) => state.inventory.inventory) || [];

    const [report, setReport] = useState('users_by_segment');
    const [segment, setSegment] = useState('');
    const [hubId, setHubId] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    /** Set by clicking a category row; the crumb above the table clears it. */
    const [category, setCategory] = useState('');
    /** The chosen product, as the whole option rather than an id, so the crumb
     *  and the drill-down heading can name it without a second lookup. */
    const [product, setProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productQuery, setProductQuery] = useState('');
    const [productsLoading, setProductsLoading] = useState(false);

    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const params = useMemo(() => {
        const p = { report };
        if (USES_SEGMENT.includes(report) && segment) p.segment = segment;
        if (USES_HUB.includes(report) && hubId) p.hub_id = hubId;
        if (USES_DATES.includes(report)) {
            // Empty means all time. Sending a blank would be an invalid date.
            if (start) p.start = start;
            if (end) p.end = end;
        }
        if (report === DRILL_REPORT && category) p.category = category;
        if (USES_PRODUCT.includes(report) && product?.product_id) p.product_id = product.product_id;
        return p;
    }, [report, segment, hubId, start, end, category, product]);

    const run = useCallback(async () => {
        /*
         * The drill-down cannot be asked without a product. The server refuses
         * it -- deliberately, because an empty answer would read as "this never
         * sold" -- so sending it anyway would turn a missing selection into a
         * red error toast.
         */
        if (report === DRILL_PRODUCT && !product?.product_id) {
            setColumns([]); setRows([]); setTitle('Orders for a product');
            return;
        }
        setLoading(true);
        try {
            const res = await CustomAxios.get('reports/users/', { params });
            setColumns(res?.data?.columns || []);
            setRows(res?.data?.rows || []);
            setTitle(res?.data?.title || '');
        } catch (e) {
            // The server explains itself -- an unknown report answers with the
            // list of valid names -- so show that rather than "something failed".
            const detail = e?.response?.data?.error || 'Could not load this report.';
            toast.current?.show({ severity: 'error', summary: 'Report failed', detail, life: 5000 });
            setColumns([]); setRows([]);
        } finally {
            setLoading(false);
        }
    }, [params]);

    // Re-run on any filter change. These are aggregates over a few thousand
    // rows, so making somebody press Run to see what they just chose would be
    // ceremony rather than protection.
    useEffect(() => { run(); }, [run]);

    /*
     * The product list, fetched through the same report registry.
     *
     * Debounced and server-side rather than pulled once and filtered here: a
     * full catalogue in a Select is a slow screen and an unusable dropdown, and
     * the search is a parameterised icontains on the server rather than a
     * filter over everything the browser happens to hold.
     */
    useEffect(() => {
        if (!USES_PRODUCT.includes(report)) return undefined;
        let live = true;
        const t = setTimeout(async () => {
            setProductsLoading(true);
            try {
                const res = await CustomAxios.get('reports/users/', {
                    params: { report: PRODUCT_OPTIONS, ...(productQuery ? { search: productQuery } : {}) },
                });
                if (live) setProductOptions(res?.data?.rows || []);
            } catch (e) {
                if (live) setProductOptions([]);
            } finally {
                if (live) setProductsLoading(false);
            }
        }, productQuery ? 260 : 0);
        return () => { live = false; clearTimeout(t); };
    }, [report, productQuery]);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await CustomAxios.get('reports/users/', {
                // `export`, not `format`: DRF reserves `format` for content
                // negotiation and answers 404 before the view is ever reached.
                params: { ...params, export: 'xlsx' },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `shino-${report}-${todayISO()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'Download failed',
                detail: 'Could not build the spreadsheet.', life: 5000,
            });
        } finally {
            setDownloading(false);
        }
    };

    const cards = useMemo(() => summaryFor(report, rows), [report, rows]);
    const bars = useMemo(() => chartFor(report, rows), [report, rows]);
    const barMax = useMemo(() => Math.max(1, ...bars.map((b) => b[1])), [bars]);

    /** Share of the column total, for the inline bar in the table. */
    const shareBase = useMemo(() => {
        const key = columns.includes('users') ? 'users'
            : columns.includes('signups') ? 'signups'
            : columns.includes('orders') ? 'orders' : null;
        if (!key) return 0;
        return rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    }, [rows, columns]);

    const heading = (c) => String(c).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
    const isNumeric = (c) => [
        'users', 'orders', 'customers', 'signups', 'share', 'id', 'amount', 'order_id',
        'units', 'revenue', 'line_value', 'order_amount', 'unmeasured',
    ].includes(c);
    const shareColumn = columns.includes('users') ? 'users'
        : columns.includes('signups') ? 'signups'
        : columns.includes('orders') ? 'orders' : null;

    const cell = (col, row) => {
        const value = row[col];
        if (value === null || value === undefined || value === '') return '—';

        if (col === 'segment') {
            return (
                <Box component="span" sx={{
                    display: "inline-block", fontSize: "11px", fontWeight: 750, borderRadius: "999px",
                    padding: "3px 10px", color: "#fff", whiteSpace: "nowrap",
                    backgroundColor: SEGMENT_COLOUR[value] || MUTED,
                }}>{value}</Box>
            );
        }

        // The share column carries a bar as well as the number: relative size
        // should not need arithmetic.
        if (col === shareColumn) {
            const v = Number(value) || 0;
            const pct = shareBase ? (v / shareBase) * 100 : 0;
            const colour = SEGMENT_COLOUR[row.segment] || PINK;
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end" }}>
                    <Box sx={{ flex: "0 0 80px", height: "6px", borderRadius: "999px", backgroundColor: CHIP, overflow: "hidden" }}>
                        <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: "999px", backgroundColor: colour }} />
                    </Box>
                    <Box component="span" sx={{ minWidth: "52px", textAlign: "right", color: v === 0 ? "#9CA3AF" : INK }}>
                        {v.toLocaleString('en-IN')}
                    </Box>
                </Box>
            );
        }

        if (col === 'share') return `${value}%`;
        if (['amount', 'revenue', 'line_value', 'order_amount'].includes(col)) {
            return `₹ ${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
        }
        // Whole where it is whole. "3.00 units" invites the question of what a
        // third of a unit would be.
        if (col === 'units') {
            const v = Number(value);
            return Number.isInteger(v) ? v.toLocaleString('en-IN') : v.toFixed(2);
        }
        if (col === 'unmeasured') {
            // Zero is the good case and should not draw the eye.
            if (!Number(value)) return '—';
            /*
             * On the ranking it is a count of orders whose lines could not be
             * quantified, so the number is the useful thing. On a single order
             * the number is always 1, and "1" says nothing -- what the reader
             * needs to know is that this row's 0 units means unrecorded rather
             * than none sold.
             */
            if (report !== DRILL_PRODUCT) return String(value);
            return (
                <Box component="span" sx={{
                    display: "inline-block", fontSize: 10, fontWeight: 700,
                    letterSpacing: ".05em", textTransform: "uppercase",
                    color: "#B4472F", backgroundColor: "#FDF0EC",
                    border: "1px solid #F5D9D1", borderRadius: "5px", padding: "2px 6px",
                    whiteSpace: "nowrap",
                }}>qty not recorded</Box>
            );
        }
        return String(value);
    };

    return (
        <Box sx={{ padding: { xs: "12px", md: "22px" }, display: "flex", flexDirection: "column", gap: "18px" }}>
            <Toast ref={toast} />

            <Box>
                <Typography sx={{ fontSize: "21px", fontWeight: 750, letterSpacing: "-0.02em", color: INK }}>
                    Reports
                </Typography>
                <Typography sx={{ fontSize: "13px", color: MUTED, marginTop: "2px" }}>
                    Segments are worked out from order count and recency the moment you ask, so
                    nothing has to be re-run to stay current.
                </Typography>
            </Box>

            {/* ---- the four figures ---- */}
            {cards.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(158px, 1fr))", gap: "12px" }}>
                    {cards.map((c) => (
                        <Box key={c.label} sx={{
                            position: "relative", overflow: "hidden", backgroundColor: "#fff",
                            border: `1px solid ${LINE}`, borderRadius: "14px", padding: "13px 14px 12px 17px",
                        }}>
                            <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", backgroundColor: TONE[c.tone] || PINK }} />
                            <Typography sx={{ fontSize: "10.5px", letterSpacing: "0.08em", textTransform: "uppercase", color: MUTED, fontWeight: 700 }}>
                                {c.label}
                            </Typography>
                            <Typography sx={{ fontSize: "25px", fontWeight: 800, letterSpacing: "-0.02em", marginTop: "3px", fontVariantNumeric: "tabular-nums", color: INK, lineHeight: 1.15 }}>
                                {c.value}
                            </Typography>
                            <Typography sx={{ fontSize: "11.5px", color: MUTED }}>{c.detail}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ---- filters ---- */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center",
                backgroundColor: CHIP, border: `1px solid ${LINE}`, borderRadius: "12px", padding: "10px 12px",
            }}>
                <Box sx={{ display: "flex", backgroundColor: "#fff", border: `1px solid ${LINE}`, borderRadius: "10px", padding: "3px", gap: "2px", flexWrap: "wrap" }}>
                    {REPORTS.map((r) => (
                        <Button key={r.key} size="small"
                            onClick={() => {
                                setReport(r.key);
                                setCategory('');
                                // The product survives moving between the ranking and
                                // the drill-down, and is dropped on the way out to any
                                // report that cannot use it.
                                if (!USES_PRODUCT.includes(r.key)) setProduct(null);
                            }}
                            sx={{
                                textTransform: "none", minWidth: 0, fontSize: "12.5px", fontWeight: 650,
                                padding: "6px 13px", borderRadius: "8px", whiteSpace: "nowrap",
                                backgroundColor: report === r.key ? PINK : "transparent",
                                color: report === r.key ? "#fff" : MUTED,
                                "&:hover": { backgroundColor: report === r.key ? PINK : "#F3F0F6" },
                            }}>
                            {r.label}
                        </Button>
                    ))}
                </Box>

                {USES_SEGMENT.includes(report) && (
                    <FormControl size="small" sx={{ minWidth: 186 }}>
                        <InputLabel id="seg-l" sx={{ fontSize: 13 }}>Segment</InputLabel>
                        <Select labelId="seg-l" label="Segment" value={segment}
                            onChange={(e) => setSegment(e.target.value)}
                            sx={{ backgroundColor: "#fff", fontSize: 13, height: 38 }}>
                            <MenuItem value="" sx={{ fontSize: 13 }}>All segments</MenuItem>
                            {SEGMENTS.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                )}

                {USES_HUB.includes(report) && (
                    <FormControl size="small" sx={{ minWidth: 176 }}>
                        <InputLabel id="hub-l" sx={{ fontSize: 13 }}>Hub</InputLabel>
                        <Select labelId="hub-l" label="Hub" value={hubId}
                            onChange={(e) => setHubId(e.target.value)}
                            sx={{ backgroundColor: "#fff", fontSize: 13, height: 38 }}>
                            <MenuItem value="" sx={{ fontSize: 13 }}>All hubs</MenuItem>
                            {inventory.map((h) => (
                                <MenuItem key={h.value ?? h.id} value={h.value ?? h.id} sx={{ fontSize: 13 }}>
                                    {h.name || h.place || `Hub ${h.value ?? h.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {USES_PRODUCT.includes(report) && (
                    <Autocomplete
                        size="small"
                        sx={{ minWidth: 248 }}
                        options={productOptions}
                        loading={productsLoading}
                        value={product}
                        // Compared by id, not by object identity: the options list
                        // is refetched as you type, so the selected product is a
                        // different object from the one now in the list.
                        isOptionEqualToValue={(a, b) => a?.product_id === b?.product_id}
                        getOptionLabel={(o) => o?.product || ''}
                        onChange={(_, value) => setProduct(value)}
                        onInputChange={(_, value, reason) => {
                            if (reason !== 'reset') setProductQuery(value);
                        }}
                        renderOption={(props, o) => (
                            <li {...props} key={o.product_id}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                                    <Box sx={{ fontSize: 13, color: INK }}>
                                        {o.product}
                                        {/* A retired product still has last quarter's orders
                                            behind it, so it stays selectable and says so. */}
                                        {!o.active && (
                                            <Box component="span" sx={{
                                                marginLeft: "7px", fontSize: 10, fontWeight: 700,
                                                letterSpacing: ".06em", textTransform: "uppercase",
                                                color: MUTED, border: `1px solid ${LINE}`,
                                                borderRadius: "4px", padding: "1px 5px",
                                            }}>inactive</Box>
                                        )}
                                    </Box>
                                    <Box sx={{ fontSize: 11, color: MUTED }}>{o.category}</Box>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label={report === DRILL_PRODUCT ? 'Product' : 'Product (optional)'}
                                InputLabelProps={{ ...params.InputLabelProps, sx: { fontSize: 13 } }}
                                sx={{ backgroundColor: "#fff" }} />
                        )}
                    />
                )}

                {USES_DATES.includes(report) && (
                    <>
                        <TextField size="small" type="date" label="From" value={start}
                            onChange={(e) => setStart(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
                            sx={{ backgroundColor: "#fff", minWidth: 158 }} />
                        <TextField size="small" type="date" label="To" value={end}
                            onChange={(e) => setEnd(e.target.value)}
                            InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
                            sx={{ backgroundColor: "#fff", minWidth: 158 }} />
                    </>
                )}

                <Button variant="contained" size="small" startIcon={<DownloadIcon />}
                    onClick={download} disabled={downloading || loading || !rows.length}
                    sx={{
                        marginLeft: "auto", textTransform: "none", fontSize: 12.5, fontWeight: 700,
                        backgroundColor: INK, borderRadius: "10px", padding: "8px 16px",
                        "&:hover": { backgroundColor: "#000" },
                    }}>
                    {downloading ? 'Preparing…' : 'Download .xlsx'}
                </Button>
            </Box>

            {/* ---- chart, only where the shape says something ---- */}
            {!loading && bars.length > 1 && (
                <Box sx={{
                    backgroundColor: "#fff", border: `1px solid ${LINE}`, borderRadius: "14px",
                    padding: "18px 18px 12px",
                }}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px", height: "160px" }}>
                        {bars.map(([label, value]) => (
                            <Box key={label} sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 750, fontVariantNumeric: "tabular-nums", color: value === 0 ? "#9CA3AF" : INK }}>
                                    {Number(value).toLocaleString('en-IN')}
                                </Typography>
                                <Box sx={{ width: "100%", height: "112px", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                                    <Box sx={{
                                        width: "100%", maxWidth: "54px",
                                        // A visible stub at zero, so an empty hub still reads as a
                                        // bar with nothing in it rather than as a missing column.
                                        height: `${Math.max(3, (Number(value) / barMax) * 110)}px`,
                                        borderRadius: "8px 8px 3px 3px",
                                        backgroundColor: SEGMENT_COLOUR[`${label} User`] || SEGMENT_COLOUR[label] || PINK,
                                        opacity: value === 0 ? 0.35 : 1,
                                    }} />
                                </Box>
                                <Typography title={label} sx={{
                                    fontSize: "10.5px", color: MUTED, textAlign: "center", maxWidth: "100%",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }}>{label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* ---- the rows ---- */}
            <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden", backgroundColor: "#fff" }}>
                <Box sx={{
                    padding: "11px 16px", borderBottom: `1px solid ${LINE}`,
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap",
                }}>
                    {report === DRILL_REPORT ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 13 }}>
                            <Box component="span" onClick={() => { setReport('orders_by_category'); setCategory(''); }}
                                sx={{ color: PINK, fontWeight: 700, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                                ← All categories
                            </Box>
                            <Box component="span" sx={{ color: MUTED }}>·</Box>
                            <Box component="span" sx={{ fontWeight: 700, color: INK }}>{category || '—'}</Box>
                        </Box>
                    ) : report === DRILL_PRODUCT ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 13 }}>
                            <Box component="span" onClick={() => setReport('orders_by_product')}
                                sx={{ color: PINK, fontWeight: 700, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                                ← All products
                            </Box>
                            <Box component="span" sx={{ color: MUTED }}>·</Box>
                            <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                                {product?.product || 'Choose a product'}
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>{title || '—'}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "44px" }}>
                        <CircularProgress size={26} sx={{ color: PINK }} />
                    </Box>
                ) : !rows.length ? (
                    <Box sx={{ padding: "38px", textAlign: "center", color: MUTED, fontSize: 13 }}>
                        {report === DRILL_PRODUCT && !product
                            ? 'Choose a product to see the orders it was in.'
                            : 'No rows for this selection.'}
                    </Box>
                ) : (
                    <Box sx={{ overflowX: "auto", maxHeight: "58vh" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr>
                                    {columns.map((c) => (
                                        <th key={c} style={{
                                            position: "sticky", top: 0, zIndex: 1, backgroundColor: "#fff",
                                            textAlign: isNumeric(c) ? "right" : "left",
                                            padding: "10px 14px", borderBottom: `1px solid ${LINE}`,
                                            fontSize: "10.5px", letterSpacing: ".08em", textTransform: "uppercase",
                                            color: MUTED, whiteSpace: "nowrap",
                                        }}>{heading(c)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i}
                                        style={{
                                            transition: "background 120ms",
                                            cursor: DRILLABLE.includes(report) ? "pointer" : "default",
                                        }}
                                        onClick={
                                            report === 'orders_by_category' && r.category
                                                ? () => { setCategory(r.category); setReport(DRILL_REPORT); }
                                                : report === 'orders_by_product' && r.product_id
                                                    // The row carries product_id even though the column is
                                                    // not displayed, so the drill-down needs no lookup.
                                                    ? () => {
                                                        setProduct({ product_id: r.product_id, product: r.product, category: r.category, active: true });
                                                        setReport(DRILL_PRODUCT);
                                                    }
                                                    : undefined
                                        }
                                        onMouseEnter={(e) => { e.currentTarget.style.background = CHIP; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                                        {columns.map((c, ci) => (
                                            <td key={c} style={{
                                                textAlign: isNumeric(c) ? "right" : "left",
                                                padding: "9px 14px", borderBottom: `1px solid ${LINE}`,
                                                fontVariantNumeric: isNumeric(c) ? "tabular-nums" : "normal",
                                                // A zero is a finding, not a gap. Greyed so it reads
                                                // as "none" rather than as missing data.
                                                color: r[c] === 0 ? "#9CA3AF" : INK,
                                                whiteSpace: "nowrap",
                                            }}>
                                                {cell(c, r)}
                                                {DRILLABLE.includes(report) && ci === columns.length - 1 ? (
                                                    <Box component="span" sx={{ color: PINK, fontWeight: 700, marginLeft: "10px" }}>›</Box>
                                                ) : null}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default UserReports;
